// ============================================
// SME Dashboard Page
// Real escalation data — Dashboard, Escalations, SEE Discussions, Archive
// ============================================

import { useState, useEffect, useRef } from 'react';
import AppShell from '../layout/AppShell';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';

// ── Animated counter ──
function useAnimatedCounter(target: number, duration = 1000) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>();
  useEffect(() => {
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.round(target * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);
  return value;
}

function AnimatedStat({ icon, label, value, suffix, color, delay }: {
  icon: string; label: string; value: number; suffix?: string; color: string; delay: number;
}) {
  const displayed = useAnimatedCounter(value);
  return (
    <div className="glass-card stat-card animate-float-in" style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <span className="stat-card__label" style={{ margin: 0 }}>{label}</span>
        <span style={{ fontSize: '22px', filter: `drop-shadow(0 0 8px ${color})` }}>{icon}</span>
      </div>
      <div className="stat-card__value" style={{ color, textShadow: `0 0 24px ${color}55` }}>
        {displayed}{suffix || ''}
      </div>
    </div>
  );
}

export default function SMEDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [allEscalations, setAllEscalations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [thread, setThread] = useState<any>(null);
  const [replyText, setReplyText] = useState('');
  const [closeText, setCloseText] = useState('');
  const [toast, setToast] = useState<{ type: string; message: string } | null>(null);

  const showToast = (type: string, message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => { loadEscalations(); }, []);

  const loadEscalations = async () => {
    setLoading(true);
    try {
      const data = await api.get<any>('/escalations');
      setAllEscalations(data.escalations || []);
    } catch { }
    setLoading(false);
  };

  const loadThread = async (id: string) => {
    try {
      const data = await api.get<any>(`/escalations/${id}`);
      setThread(data);
      setExpandedId(id);
    } catch { }
  };

  const sendReply = async (escalationId: string) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/escalations/${escalationId}/messages`, { message: replyText });
      setReplyText('');
      showToast('success', 'Reply sent');
      loadThread(escalationId);
      loadEscalations();
    } catch (e: any) { showToast('error', e.message); }
  };

  const updateStatus = async (escalationId: string, status: string, message?: string) => {
    try {
      await api.put(`/escalations/${escalationId}/status`, { status, message });
      showToast('success', `Status updated to ${status.replace(/_/g, ' ')}`);
      setExpandedId(null);
      setCloseText('');
      loadEscalations();
    } catch (e: any) { showToast('error', e.message); }
  };

  // Categorize
  const openEscalations = allEscalations.filter(e => e.status === 'open');
  const seeDiscussions = allEscalations.filter(e => e.status === 'see_discussion');
  const closedEscalations = allEscalations.filter(e => e.status === 'closed');

  const statusColors: Record<string, string> = { open: 'critical', see_discussion: 'in_progress', closed: 'success' };

  const renderEscalationCard = (esc: any, actions: 'escalations' | 'see-discussions' | 'archive') => (
    <div key={esc.id} className="glass-card glass-card--hover" style={{ padding: 'var(--space-4)', cursor: 'pointer' }}
      onClick={() => expandedId === esc.id ? setExpandedId(null) : loadThread(esc.id)}>
      <div className="flex-between" style={{ marginBottom: 'var(--space-2)' }}>
        <div style={{ fontWeight: 700 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-xs)', marginRight: 'var(--space-2)' }}>
            {esc.caseNumber}
          </span>
          <span className={`glass-badge glass-badge--${statusColors[esc.status] || 'open'}`}>
            {esc.status.replace(/_/g, ' ')}
          </span>
        </div>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {new Date(esc.createdAt).toLocaleDateString()}
        </span>
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
        {esc.description}
      </div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
        Engineer: {esc.engineerName}
        {esc.lastMessage && <> • Last: "{esc.lastMessage.substring(0, 60)}{esc.lastMessage.length > 60 ? '...' : ''}"</>}
      </div>

      {expandedId === esc.id && thread && (
        <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}
          onClick={(e) => e.stopPropagation()}>
          {/* Message thread */}
          <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: 'var(--space-3)' }}>
            {thread.messages?.map((msg: any) => (
              <div key={msg.id} style={{
                padding: 'var(--space-3)', marginBottom: 'var(--space-2)',
                background: msg.senderRole === 'sme' ? 'rgba(77, 150, 255, 0.08)' : 'rgba(220, 20, 60, 0.06)',
                borderRadius: 'var(--radius-md)', borderLeft: `3px solid ${msg.senderRole === 'sme' ? '#4D96FF' : '#DC143C'}`,
              }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
                  <strong>{msg.senderName}</strong> ({msg.senderRole}) • {new Date(msg.createdAt).toLocaleString()}
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)' }}>{msg.message}</div>
              </div>
            ))}
            {thread.messages?.length === 0 && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
                No messages yet — be the first to reply
              </div>
            )}
          </div>

          {/* Reply + action buttons */}
          {actions !== 'archive' && (
            <>
              <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                <input className="glass-input" style={{ flex: 1 }} placeholder="Type a reply..."
                  value={replyText} onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendReply(esc.id)} />
                <button className="glass-btn glass-btn--primary glass-btn--sm" onClick={() => sendReply(esc.id)} disabled={!replyText.trim()}>
                  Send
                </button>
              </div>

              <div className="flex-gap-2">
                {actions === 'escalations' && esc.status === 'open' && (
                  <button className="glass-btn glass-btn--secondary glass-btn--sm"
                    onClick={() => updateStatus(esc.id, 'see_discussion')}>
                    📋 Mark for SEE Discussion
                  </button>
                )}
                {actions === 'see-discussions' && esc.status === 'see_discussion' && (
                  <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 1, alignItems: 'end' }}>
                    <input className="glass-input" style={{ flex: 1 }} placeholder="Closing note / outcome..."
                      value={closeText} onChange={(e) => setCloseText(e.target.value)} />
                    <button className="glass-btn glass-btn--primary glass-btn--sm"
                      onClick={() => updateStatus(esc.id, 'closed', closeText || 'Resolved after SEE discussion')}
                      disabled={!closeText.trim()}>
                      ✅ Close Escalation
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderDashboard = () => (
    <>
      <div className="grid-stats" style={{ marginBottom: 'var(--space-6)' }}>
        <AnimatedStat icon="🔴" label="Open Escalations" value={openEscalations.length} color="#DC143C" delay={0} />
        <AnimatedStat icon="💬" label="SEE Discussion" value={seeDiscussions.length} color="#4D96FF" delay={80} />
        <AnimatedStat icon="✅" label="Resolved" value={closedEscalations.length} color="#00D46A" delay={160} />
        <AnimatedStat icon="📊" label="Total" value={allEscalations.length} color="#A855F7" delay={240} />
      </div>

      {openEscalations.length > 0 && (
        <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>
            🔴 Open Escalations (Needs Attention)
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {openEscalations.slice(0, 5).map((esc) => renderEscalationCard(esc, 'escalations'))}
          </div>
        </div>
      )}
    </>
  );

  const renderEscalationsTab = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>🔴 Open Escalations</h2>
        <button className="glass-btn glass-btn--secondary glass-btn--sm" onClick={loadEscalations}>Refresh</button>
      </div>
      {openEscalations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">✅</div>
          <div className="empty-state__title">All clear</div>
          <div className="empty-state__text">No open escalations at the moment.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {openEscalations.map((esc) => renderEscalationCard(esc, 'escalations'))}
        </div>
      )}
    </div>
  );

  const renderSeeDiscussions = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>💬 SEE Discussions</h2>
        <button className="glass-btn glass-btn--secondary glass-btn--sm" onClick={loadEscalations}>Refresh</button>
      </div>
      {seeDiscussions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">💬</div>
          <div className="empty-state__title">No discussions pending</div>
          <div className="empty-state__text">Escalations marked for SEE discussion will appear here.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {seeDiscussions.map((esc) => renderEscalationCard(esc, 'see-discussions'))}
        </div>
      )}
    </div>
  );

  const renderArchive = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>📦 Escalation Archive</h2>
        <button className="glass-btn glass-btn--secondary glass-btn--sm" onClick={loadEscalations}>Refresh</button>
      </div>
      {closedEscalations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">📦</div>
          <div className="empty-state__title">No closed escalations</div>
          <div className="empty-state__text">Closed escalations will be archived here.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {closedEscalations.map((esc) => renderEscalationCard(esc, 'archive'))}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (loading) return <div className="flex-center" style={{ padding: 'var(--space-16)' }}><div className="spinner spinner--lg" /></div>;
    switch (activeTab) {
      case 'escalations': return renderEscalationsTab();
      case 'see-discussions': return renderSeeDiscussions();
      case 'escalation-archive': return renderArchive();
      default: return renderDashboard();
    }
  };

  return (
    <AppShell role="sme" activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}

      {toast && (
        <div className="glass-toast-container">
          <div className={`glass-toast glass-toast--${toast.type}`}>
            {toast.type === 'success' ? '✅' : '❌'} {toast.message}
          </div>
        </div>
      )}
    </AppShell>
  );
}
